<script>
  // Components
  import ButtonWithTooltip from "@sourcewhale/shared/svelte-design-system/components/atoms/ButtonWithTooltip.svelte";
  import Dropdown from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/Dropdown.svelte";
  import ListItem from "@sourcewhale/shared/svelte-design-system/components/molecules/Dropdown/ListItem.svelte";
  // Stores
  import {
    emailSearchKinds,
    lastEnrichTime,
    suggestionsStore,
    todosModalItemStore,
  } from "../stores";
  // Utils
  import { hasLicense } from "@sourcewhale/shared/src/assets/js/src/general/licenses";
  import { showToast } from "@sourcewhale/shared/src/assets/js/src/general/utils";
  import { userStore } from "@sourcewhale/shared/src/svelte/api-stores/user-store";
  import {
    getSearches,
    startSearches,
  } from "@sourcewhale/shared/src/svelte/api/candidates";
  import { extractGetSearchesEmails } from "../utils";

  export let enrichmentText = "";

  let isEnriching = false;
  let firstEnrichmentStarted = false;
  let leadingIcon = "fa-regular fa-magnifying-glass";

  let messageTimer;
  let closeDropdown;
  let searchesResult = new Set();

  $: hasDataLicense = hasLicense($userStore, "data");

  const initiateGetResults = async (searchKind) => {
    if (!searchKind) {
      isEnriching = false;
      return null;
    }

    const res = await getSearches({
      resultIdsMap: { [$todosModalItemStore.candidateId]: [searchKind] },
    });

    const candidateResults = res.results[$todosModalItemStore.candidateId];
    if (candidateResults?.[searchKind]) {
      isEnriching = false;
      searchesResult = extractGetSearchesEmails(res);
      return null;
    }

    // Wait 1 second and try again
    await new Promise((resolve) => {
      setTimeout(resolve, 1000);
    });
    return initiateGetResults(searchKind);
  };

  const handleItemClick = async (kind, shouldCloseDropdown = false) =>
    startSearches({
      candidateIds: [$todosModalItemStore.candidateId],
      contactTypes: [kind],
    }).then(() => {
      isEnriching = true;
      initiateGetResults(kind);
      if (shouldCloseDropdown) {
        closeDropdown();
      }
    });

  const handleEnrichmentStarted = () => {
    firstEnrichmentStarted = true;
    leadingIcon = "Spinner color-textWhite";
    enrichmentText = "Searching for results...";
    messageTimer = setTimeout(() => {
      enrichmentText =
        "We are still searching. Retrieving contact details from your connected data provider is taking longer than expected...";
    }, 30000);
  };

  // firstEnrichmentStarted used to prevent toast from showing on initial load
  const handleEnrichmentFinished = () => {
    if (!firstEnrichmentStarted) return;

    if (
      [...searchesResult].some((element) => !$suggestionsStore.has(element))
    ) {
      $lastEnrichTime = Date.now();
      showToast({
        title: "Success!",
        subtitle: "Contact details enriched.",
        type: "success",
      });
    } else {
      showToast({
        title: "No search results",
        subtitle: "No new contact details have been found.",
        type: "info",
      });
    }

    clearTimeout(messageTimer);
    leadingIcon = "fa-regular fa-magnifying-glass";
    enrichmentText = "";
  };

  // React to isEnriching changes
  $: {
    if (isEnriching) {
      handleEnrichmentStarted();
    } else {
      handleEnrichmentFinished();
    }
  }
</script>

{#if $emailSearchKinds.length === 1}
  {@const disabled = $emailSearchKinds[0][0] === "email" && !hasDataLicense}
  <ButtonWithTooltip
    kind="primary"
    size="small"
    {leadingIcon}
    onclick={() => handleItemClick($emailSearchKinds[0][0])}
    placement="top"
    {disabled}
  >
    <span slot="tooltip">
      Enrich emails using your connected data providers
    </span>
    <span>Enrich via {$emailSearchKinds[0][1].title}</span>
  </ButtonWithTooltip>
{:else if $emailSearchKinds.length > 1}
  <Dropdown
    kind="primary"
    placement="bottom-end"
    tooltipPlacement="top"
    {leadingIcon}
    trailingIcon=""
    dynamicWidth={false}
    bind:close={closeDropdown}
  >
    <span slot="tooltip">
      Enrich emails using your connected data providers
    </span>
    <span slot="button">Enrich Email</span>
    {#each $emailSearchKinds as [kind, item]}
      {@const disabled = kind === "email" && !hasDataLicense}
      {@const src = "/assets/img/licenses/{kind.split('--')[0]}.png"}
      {@const onclick = () => handleItemClick(kind, true)}
      {@const text = `Enrich via ${item.title}`}
      {#if disabled}
        <ListItem {src} {onclick} {disabled}>
          {text}
          <span slot="tooltip"
            >Enriching via SourceWhale requires a data license.</span
          >
        </ListItem>
      {:else}
        <ListItem {src} {onclick} {disabled}>
          {text}
        </ListItem>
      {/if}
    {/each}
  </Dropdown>
{/if}
